<template>
  <div class="product-page">
    <!-- Banner Section -->
    <div class="banner-section">
      <div class="banner-container">
        <div class="banner-left">
          <img src="@/assets/product/light530.png"
               alt="Light 530"
               class="product-img" />
          <div class="product-name">{{ $t('product.light530.name') }}</div>
        </div>
        <div class="banner-right">
          <h1 class="banner-title">{{ $t('product.light530.bannerTitle') }}</h1>
          <div class="feature-list">
            <div class="feature-item"
                 v-for="(item, index) in bannerFeatures"
                 :key="index">
              <div class="feature-icon-wrapper">
                <img :src="item.icon"
                     alt="icon"
                     class="feature-icon" />
              </div>
              <div class="feature-text">{{ item.text }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Advantages Section -->
    <div class="advantages-section">
      <h2 class="section-title">{{ $t('product.light530.sections.advantages') }}</h2>
      <div class="advantage-list">
        <div class="advantage-item"
             v-for="(item, index) in advantages"
             :key="index"
             :class="{ 'reverse': index % 2 !== 0 }">
          <!-- Text Area -->
          <div class="text-area">
            <div class="tag"
                 :style="{ backgroundColor: item.tagColor }">
              <img :src="item.tagIcon"
                   alt="icon"
                   class="tag-icon" />
              <span class="tag-title">{{ item.tagTitle }}</span>
            </div>
            <div class="desc">{{ item.desc }}</div>
          </div>

          <!-- Image Area -->
          <div class="image-area">
            <img :src="item.image"
                 :alt="item.tagTitle"
                 class="adv-img" />
          </div>
        </div>
      </div>
      <div class="advantage-software">
        <div class="software-image-area">
          <img :src="softwareSectionImage"
               :alt="$t('product.light530.softwareSection.title')"
               class="software-image" />
        </div>
        <div class="software-content">
          <h3 class="software-title">{{ $t('product.light530.softwareSection.title') }}</h3>
          <div class="software-items">
            <div class="software-item"
                 v-for="(text, index) in softwareSectionItems"
                 :key="index">
              <div class="software-item-icon"
                   :class="`variant-${index}`">
                <img :src="softwareItemIcons[index] || softwareItemIcons[0]"
                     alt=""
                     class="software-item-icon-img" />
              </div>
              <div class="software-item-text">{{ text }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- AI Banner Section -->
    <div class="ai-banner">
      <div class="ai-banner-content">
        <h2 class="ai-title">{{ $t('product.light530.sections.aiTitle') }}</h2>
        <p class="ai-subtitle">{{ $t('product.light530.sections.aiSubtitle') }}</p>
      </div>
    </div>

    <!-- Carousel Section -->
    <div class="carousel-section">
      <h2 class="carousel-title">{{ $t('product.light530.sections.carouselTitle') }}</h2>
      <div class="carousel-container">
        <div class="carousel-track"
             :style="trackStyle">
          <!-- 原始列表 -->
          <div class="carousel-item"
               v-for="(item, index) in carouselItems"
               :key="'original-' + index">
            <img :src="item.image"
                 :alt="item.title"
                 class="carousel-img" />
          </div>
          <!-- 复制一份用于无缝循环 -->
          <div class="carousel-item"
               v-for="(item, index) in carouselItems"
               :key="'clone-' + index">
            <img :src="item.image"
                 :alt="item.title"
                 class="carousel-img" />
          </div>
        </div>
      </div>
    </div>

    <!-- Specs Section -->
    <div class="specs-section">
      <h2 class="section-title">{{ $t('product.light530.sections.specsTitle') }}</h2>
      <div class="specs-container">
        <!-- 左列 -->
        <div class="specs-column">
          <div class="spec-row"
               v-for="(item, index) in leftSpecs"
               :key="index">
            <div class="spec-label">{{ item.label }}</div>
            <div class="spec-value">{{ item.value }}</div>
          </div>
        </div>

        <!-- 右列 -->
        <div class="specs-column">
          <!-- 水冷系统 -->
          <div class="spec-group-header">{{ $t('product.light530.specs.groups.waterCoolingTitle') }}</div>
          <div class="spec-row"
               v-for="(item, index) in waterCoolingSpecs"
               :key="'wc-'+index">
            <div class="spec-label">{{ item.label }}</div>
            <div class="spec-value">{{ item.value }}</div>
          </div>

          <!-- 气泵参数 -->
          <div class="spec-group-header">{{ $t('product.light530.specs.groups.airPumpTitle') }}</div>
          <div class="spec-value-only">{{ $t('product.light530.specs.groups.airPumpValueOnly') }}</div>
          <div class="spec-row"
               v-for="(item, index) in airPumpSpecs"
               :key="'ap-'+index">
            <div class="spec-label">{{ item.label }}</div>
            <div class="spec-value">{{ item.value }}</div>
          </div>

          <!-- 吸风系统 -->
          <div class="spec-group-header">{{ $t('product.light530.specs.groups.exhaustTitle') }}</div>
          <div class="spec-row"
               v-for="(item, index) in exhaustSpecs"
               :key="'ex-'+index">
            <div class="spec-label">{{ item.label }}</div>
            <div class="spec-value">{{ item.value }}</div>
          </div>

          <!-- 过滤系统 -->
          <div class="spec-group-header">{{ $t('product.light530.specs.groups.filterTitle') }}</div>
          <div class="spec-full-row">{{ $t('product.light530.specs.groups.filterValue') }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const LIGHT530_ADVANTAGE_MEDIA = [
  {
    tagIcon: require('@/assets/product/advicon.png'),
    tagColor: '#448EF7',
    image: require('@/assets/product/adv1.png')
  },
  {
    tagIcon: require('@/assets/product/advicon.png'),
    tagColor: '#FBBC05',
    image: require('@/assets/product/adv2.png')
  },
  {
    tagIcon: require('@/assets/product/advicon.png'),
    tagColor: '#8BC34A',
    image: require('@/assets/product/adv3.png')
  }
]

const LIGHT530_CAROUSEL_IMAGES = [
  require('@/assets/product/530-1.png'),
  require('@/assets/product/530-2.png'),
  require('@/assets/product/530-3.png'),
  require('@/assets/product/530-4.png'),
  require('@/assets/product/530-5.png'),
  require('@/assets/product/530-6.png'),
  require('@/assets/product/530-7.png'),
  require('@/assets/product/530-8.png'),
]

export default {
  name: 'ProductLight530',
  data() {
    return {
      // 轮播控制
      scrollPosition: 0,
      scrollSpeed: 1, // 滚动速度，像素/帧
      animationFrameId: null,
    }
  },
  computed: {
    light530Copy() {
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'zh-CN'
      const messages = this.$i18n && this.$i18n.getLocaleMessage ? this.$i18n.getLocaleMessage(locale) : {}
      return (messages.product && messages.product.light530) || {}
    },
    softwareSectionItems() {
      return this.light530Copy.softwareSection && this.light530Copy.softwareSection.items ? this.light530Copy.softwareSection.items : []
    },
    softwareSectionImage() {
      return require('@/assets/product/adv4.png')
    },
    softwareItemIcons() {
      return [
        require('@/assets/product/advicon2.png'),
        require('@/assets/product/advicon3.png'),
        require('@/assets/product/advicon4.png')
      ]
    },
    leftSpecs() {
      return this.light530Copy.specs && this.light530Copy.specs.leftSpecs ? this.light530Copy.specs.leftSpecs : []
    },
    waterCoolingSpecs() {
      return this.light530Copy.specs && this.light530Copy.specs.waterCoolingSpecs ? this.light530Copy.specs.waterCoolingSpecs : []
    },
    airPumpSpecs() {
      return this.light530Copy.specs && this.light530Copy.specs.airPumpSpecs ? this.light530Copy.specs.airPumpSpecs : []
    },
    exhaustSpecs() {
      return this.light530Copy.specs && this.light530Copy.specs.exhaustSpecs ? this.light530Copy.specs.exhaustSpecs : []
    },
    bannerFeatures() {
      const texts = this.light530Copy.bannerFeatures || []
      return texts.map((text) => ({
        icon: require('@/assets/product/icon.png'),
        text
      }))
    },
    advantages() {
      const items = this.light530Copy.advantages || []
      return items.map((item, index) => ({
        tagTitle: item.tagTitle,
        tagIcon: LIGHT530_ADVANTAGE_MEDIA[index] ? LIGHT530_ADVANTAGE_MEDIA[index].tagIcon : '',
        tagColor: LIGHT530_ADVANTAGE_MEDIA[index] ? LIGHT530_ADVANTAGE_MEDIA[index].tagColor : '#448EF7',
        desc: item.desc,
        image: LIGHT530_ADVANTAGE_MEDIA[index] ? LIGHT530_ADVANTAGE_MEDIA[index].image : ''
      }))
    },
    carouselItems() {
      const titles = this.light530Copy.carouselTitles || []
      return LIGHT530_CAROUSEL_IMAGES.map((image, index) => ({
        title: titles[index] || `Item ${index + 1}`,
        image
      }))
    },
    trackStyle() {
      return {
        transform: `translateX(-${this.scrollPosition}px)`
      }
    }
  },
  mounted() {
    this.startCarousel()
  },
  beforeDestroy() {
    this.stopCarousel()
  },
  methods: {
    startCarousel() {
      const animate = () => {
        this.scrollPosition += this.scrollSpeed

        // 假设每个item宽316 + 间距20 = 336px (这里需要根据CSS实际值调整)
        // 这里的 336 是 (316 + 20) 的估算值，实际在CSS中设置
        const itemWidth = 336
        const totalWidth = this.carouselItems.length * itemWidth

        // 当滚动到一半（原始列表结束）时，重置回起点，实现无缝
        if (this.scrollPosition >= totalWidth) {
          this.scrollPosition = 0
        }

        this.animationFrameId = requestAnimationFrame(animate)
      }
      this.animationFrameId = requestAnimationFrame(animate)
    },
    stopCarousel() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId)
      }
    }
  }
}
</script>

<style scoped>
/* ... existing styles ... */

/* AI Banner Section */
.ai-banner {
  width: 100%;
  height: 600px;
  background-image: url("@/assets/product/aibanner.png"); /* AI Tech bg */
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-banner-content {
  width: 1200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: -150px; /* 根据图示大概位置调整 */
}

.ai-title {
  font-size: 34px;
  color: #333;
  margin-bottom: -20px;
  font-weight: normal; /* 图示看起来不是特别粗，或者根据需要调整 */
}

.ai-subtitle {
  font-size: 34px;
  color: #333;
  font-weight: normal;
}

/* Carousel Section */
.carousel-section {
  width: 100%;
  height: 650px;
  background-color: #f4f6fa;
  padding-top: 80px;
  overflow: hidden; /* 隐藏超出部分 */
}

.carousel-title {
  font-size: 34px;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin-bottom: 60px;
}

.carousel-container {
  width: 100%;
  /* 不需要限制1200px，因为是全屏流式轮播 */
  overflow: hidden;
}

.carousel-track {
  display: flex;
  gap: 20px; /* 间距 */
  width: max-content; /* 让宽度自适应内容 */
  padding-left: 50px; /* 初始偏移一点，避免贴边 */
  will-change: transform; /* 优化性能 */
}

.carousel-item {
  width: 316px;
  height: 316px;
  flex-shrink: 0;
  border-radius: 30px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
}

.carousel-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-page {
  width: 100%;
  background-color: #fff;
  padding-bottom: 80px; /* 增加底部留白 */
}

/* Specs Section */
.specs-section {
  width: 1200px;
  margin: 0 auto;
  padding-top: 100px;
  padding-bottom: 50px;
}

.specs-container {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.specs-column {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.spec-row {
  display: flex;
  min-height: 50px; /* 确保行高 */
}

/* 隔行变色逻辑需要小心，因为这里是手动分组
   我们可以用 :nth-child 来做简单的条纹，
   或者给每一行单独的样式。UI图看起来是交替的灰色背景。
*/
.spec-row:nth-child(odd),
.spec-value-only:nth-child(odd),
.spec-full-row:nth-child(odd) {
  background-color: #f9f9f9; /* 浅灰 */
}

/* 左侧列统一样式 */
.specs-column:first-child .spec-row:nth-child(odd) {
  background-color: #f9f9f9;
}
.specs-column:first-child .spec-row:nth-child(even) {
  background-color: #fff;
}

/* 右侧列比较复杂，有标题穿插，建议统一给所有直接子元素加条纹 */
.specs-column > div {
  /* 默认白色 */
  background-color: #fff;
}
/* 
  但是右侧有标题行，标题行是灰色的。
  根据图示，标题行(group-header)通常有背景色 #EAEAEA
*/

.spec-group-header {
  background-color: #e6e6e6;
  font-weight: bold;
  color: #333;
  padding: 15px 20px;
  font-size: 16px;
  margin-bottom: 0; /* 紧贴下方内容 */
}

.spec-label {
  width: 180px; /* 标签宽度 */
  padding: 15px 20px;
  color: #333;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.spec-value {
  flex: 1;
  padding: 15px 20px;
  color: #666;
  display: flex;
  align-items: center;
  line-height: 1.5;
}

.spec-value-only {
  padding: 15px 20px;
  color: #666;
  background-color: #fff; /* 或根据条纹 */
}

.spec-full-row {
  padding: 15px 20px;
  color: #666;
  background-color: #f9f9f9; /* 最后一行如果是单行，可能是灰色 */
}

/* 针对左列的条纹调整：第一行是白色还是灰色？通常第一行白色 */
.specs-column:first-child .spec-row:nth-of-type(odd) {
  background-color: #f9f9f9;
}
.specs-column:first-child .spec-row:nth-of-type(even) {
  background-color: #fff;
}

/* 右列条纹：
   需要手动控制或者用复杂的 nth-child。
   为了简单还原UI图，我们假设右侧每个小块内部也是交替的。
   但右侧有 Header 隔断。
   
   简单策略：
   Header: #E6E6E6
   内容行: 交替 #fff / #f9f9f9
*/
.specs-column:last-child .spec-row:nth-of-type(odd) {
  background-color: #fff;
}
.specs-column:last-child .spec-row:nth-of-type(even) {
  background-color: #f9f9f9;
}
/* 特殊处理：气泵下方的纯值行 */
.spec-value-only {
  background-color: #fff;
}

/* Banner Section */
.banner-section {
  width: 100%;
  height: 684px;
  background-image: url("@/assets/product/banner.png"); /* Tech bg */
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-container {
  width: 1200px;
  height: 406px;
  display: flex;
  /* background-color: rgba(255, 255, 255, 0.8); Optional: for visibility if bg is dark */
}

.banner-left {
  width: 532px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.product-img {
  width: 532px;
  height: 352px;
  object-fit: contain;
}

.product-name {
  margin-top: 20px; /* Adjust as needed, user didn't specify gap but said below */
  font-size: 20px;
  color: #333;
  text-align: center;
}

.banner-right {
  flex: 1;
  padding-left: 50px; /* Adjust spacing */
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.banner-title {
  font-size: 40px;
  font-weight: bold;
  color: #333;
  margin-bottom: 30px; /* Adjust spacing */
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.feature-item {
  display: flex;
  align-items: center;
}

.feature-icon-wrapper {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.feature-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(
    0 2px 4px rgba(68, 142, 247, 0.3)
  ); /* Add blue tint hint if possible or use colored icon */
}

.feature-text {
  font-size: 20px;
  color: #333;
}

/* Advantages Section */
.advantages-section {
  width: 1200px;
  margin: 0 auto;
  padding-top: 80px;
  padding-bottom: 100px;
}

.section-title {
  font-size: 34px;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin-bottom: 80px;
}

.advantage-list {
  display: flex;
  flex-direction: column;
  gap: 100px; /* Large gap between items */
}

.advantage-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.advantage-item.reverse {
  flex-direction: row-reverse;
}

.text-area {
  width: 453px;
  height: 206px;
  display: flex;
  flex-direction: column;
  justify-content: space-between; /* To align tag top and desc bottom if needed, or just flow */
}

.tag {
  width: 283px;
  height: 62px;
  border-radius: 65px;
  display: flex;
  align-items: center;
  padding: 0 20px; /* Adjust padding */
  box-sizing: border-box;
}

.tag-icon {
  width: 42px;
  height: 42px;
  margin-right: 15px;
  object-fit: contain;
  filter: brightness(0) invert(1); /* Make white */
}

.tag-title {
  font-size: 20px;
  color: #fff;
  font-weight: bold;
}

.desc {
  width: 453px;
  height: 120px; /* Fixed height as requested */
  font-size: 16px;
  color: #333;
  line-height: 1.6;
  margin-top: auto; /* Push to bottom of container if using flex column with height */
}

.image-area {
  width: 577px;
  height: 360px;
  border-radius: 20px; /* Optional rounded corners for images */
  overflow: hidden;
}

.adv-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.advantage-software {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 100px;
  gap: 60px;
}

.software-image-area {
  width: 620px;
  height: 359px;
  flex: 0 0 auto;
}

.software-image {
  width: 100%;
  height: 100%;
  display: block;
  border-radius: 12px;
}

.software-content {
  flex: 1 1 auto;
  min-width: 0;
}

.software-title {
  font-size: 20px;
  color: #333;
  font-weight: bold;
  margin: 0 0 22px 0;
  text-align: left;
}

.software-items {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.software-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.software-item-icon {
  width: 51px;
  height: 51px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.software-item-icon.variant-0 {
  background-color: #3b82f6;
}

.software-item-icon.variant-1 {
  background-color: #f59e0b;
}

.software-item-icon.variant-2 {
  background-color: #22c55e;
}

.software-item-icon-img {
  width: 51px;
  height: 51px;
  object-fit: contain;
}

.software-item-text {
  font-size: 16px;
  color: #333;
  line-height: 1.6;
  text-align: left;
}
</style>
